import React, { useEffect, useState } from 'react';
import { Check, X, ShieldCheck, Truck, Clock, Star } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { TestimonialCard } from '../components/TestimonialCard';
import { FAQItem } from '../components/FAQItem';
import { StickyCTA } from '../components/StickyCTA';
import { useUserCity } from '../hooks/useUserCity';
export function AdvertorialPage() {
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const {
    city,
    countryCode,
    loading
  } = useUserCity('A');
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToOffer = () => {
    window.location.href = 'https://trynymera.com/products/nymera-calmrise%E2%84%A2';
  };
  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center" data-id="element-50">
        <div className="flex flex-col items-center gap-3" data-id="element-51">
          <div className="w-8 h-8 border-3 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" data-id="element-52" />
          <span className="text-sm text-slate-400" data-id="element-53">Loading article...</span>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-white text-slate-900 font-sans selection:bg-emerald-100 selection:text-emerald-900" data-id="element-54">
      {/* Navigation / Header Bar */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-40" data-id="element-55">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between" data-id="element-56">
          <div className="font-bold text-xl tracking-tight text-slate-900" data-id="element-57">
            Parenting<span className="text-emerald-600" data-id="element-58">Today</span>
          </div>
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wider border border-slate-200 px-2 py-1 rounded" data-id="element-59">
            Advertorial
          </div>
        </div>
      </header>

      {/* Main Content with Sidebar Layout */}
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12" data-id="element-60">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8" data-id="element-61">
          {/* Main Article Content */}
          <main data-id="element-62">
            {/* Article Meta */}
            <div className="mb-8 border-b border-slate-200 pb-8" data-id="element-63">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-4 flex-wrap" data-id="element-64">
                <span className="font-bold text-emerald-700 uppercase tracking-wide text-xs" data-id="element-65">
                  {countryCode} | Parenting & Health
                </span>
                <span data-id="element-66">•</span>
                <span data-id="element-67">February 14, 2025</span>
                <span data-id="element-68">•</span>
                <span data-id="element-69">142,847 views</span>
              </div>

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight mb-6 tracking-tight" data-id="element-70">
                {city} Mom Discovers Why Traditional Alarm Clocks Are Triggering
                ADHD Meltdowns (And The 30-Second Fix That Stopped Them)
              </h1>

              <p className="text-lg md:text-xl text-slate-600 italic mb-6" data-id="element-71">
                If your child wakes up screaming every morning, it's not their
                fault. Scientists just found out why regular alarms attack their
                brain.
              </p>

              <div className="flex items-center gap-3" data-id="element-72">
                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden" data-id="element-73">
                  <img src="https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=100&h=100" alt="Jessica Martinez" className="w-full h-full object-cover" data-id="element-74" />
                </div>
                <div data-id="element-75">
                  <div className="font-bold text-slate-900 text-sm" data-id="element-76">
                    By Jessica Martinez
                  </div>
                  <div className="text-slate-500 text-xs" data-id="element-77">
                    Mother to a Child with ADHD
                  </div>
                </div>
              </div>
            </div>

            {/* Article Body */}
            <article className="prose prose-lg prose-slate max-w-none font-serif" data-id="element-78">
              {/* SECTION 1 - Opening */}
              <p className="lead text-xl md:text-2xl text-slate-700 leading-relaxed font-medium mb-8" data-id="element-79">
                6:45 AM.
              </p>

              <p data-id="element-80">The alarm goes off in your child's room.</p>

              <p data-id="element-81">
                And just like that, your day is ruined before it even starts.
              </p>

              <p data-id="element-82">
                The screaming. The crying. The complete meltdown. Your child
                won't get out of bed. They're hitting. They're kicking. They
                can't calm down no matter what you say or do.
              </p>

              <p data-id="element-83">
                By 7:30 AM, you're both exhausted. You feel like a failure. Your
                child feels terrible. And you still have to get them to school
                somehow.
              </p>

              <p className="font-bold text-slate-900" data-id="element-84">Sound familiar?</p>

              <p data-id="element-85">If this is your morning routine... you're not alone.</p>

              <img src="https://cdn.magicpatterns.com/uploads/69D7CN194wtcmBq9NTJE3N/ChatGPT_Image_14_Φεβ_2026,_03_14_44_μ.μ..png" alt="Frustrated parent dealing with child's morning meltdown" className="w-full rounded-xl my-8 shadow-lg" data-id="element-86" />

              <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 md:p-6 my-8 not-prose rounded-r-lg" data-id="element-87">
                <p className="font-serif text-base md:text-lg text-emerald-900 italic font-medium m-0" data-id="element-88">
                  Over 7 million families with ADHD or autistic kids face this
                  same battle every single day. The same alarm. The same
                  meltdown. The same guilt.
                </p>
              </div>

              <p data-id="element-89">But here's what nobody told you.</p>

              <p data-id="element-90">
                It's not your child being difficult. And it's not your
                parenting.
              </p>

              <p data-id="element-91">
                New brain research just revealed the real problem. And it's been
                sitting on your child's nightstand this whole time.
              </p>

              <p data-id="element-92">
                Every time that alarm goes off, something happens inside your
                child's brain that makes everything worse. Scientists have a
                name for it. They call it the{' '}
                <strong data-id="element-93">"Auditory Overload Cascade."</strong>
              </p>

              <p data-id="element-94">And once you understand it, everything changes.</p>

              {/* SECTION 2 - The Mom Who Couldn't Take It Anymore */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-95">
                The Mom Who Couldn't Take It Anymore
              </h2>

              <p data-id="element-96">
                Sarah Mitchell is a single mom from Phoenix. She has an
                8-year-old son named Jake who has ADHD.
              </p>

              <p data-id="element-97">For years, mornings in their home were a war zone.</p>

              <p data-id="element-98">
                "I tried everything," Sarah says. "Reward charts. Earlier
                bedtimes. Special wake-up songs. I even talked to his doctor
                about sleep medication."
              </p>

              <p data-id="element-99">Nothing worked.</p>

              <p data-id="element-100">
                Jake would sleep through his alarm. Or worse, he'd wake up
                completely overwhelmed. Crying. Screaming. Unable to get ready
                for school.
              </p>

              <img src="https://cdn.magicpatterns.com/uploads/wgL378Q4HRr9dYRQHuwqcd/ChatGPT_Image_14_Φεβ_2026,_03_14_43_μ.μ..png" alt="Single mom Sarah looking hopeful" className="w-full rounded-xl my-8 shadow-lg" data-id="element-101" />

              <p data-id="element-102">
                Sarah got so many tardy slips that the school started
                threatening her with truancy charges. She felt like the worst
                mom in the world.
              </p>

              <p data-id="element-103">
                "I loved my son so much," she says, her voice cracking. "But I
                dreaded mornings. I actually dreaded waking up because I knew
                what was coming."
              </p>

              <p data-id="element-104">Then one morning, everything hit a breaking point.</p>

              <p data-id="element-105">
                Jake had such a bad meltdown that he punched a hole in his
                bedroom wall. He was sobbing. Sarah was sobbing. They were both
                late. Again.
              </p>

              <p className="font-bold text-slate-900" data-id="element-106">
                That's when Sarah decided: There has to be a better way.
              </p>

              <p data-id="element-107">
                She made an appointment with a pediatric neurologist. And what
                she learned changed everything.
              </p>

              {/* SECTION 3 - The Hidden Problem With Loud Alarms */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-108">
                The Hidden Problem With Loud Alarms
              </h2>

              <p data-id="element-109">
                "I'll never forget what the doctor told me," Sarah says. "He
                said, 'The alarm isn't waking Jake up. It's attacking him.'"
              </p>

              <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 md:p-6 my-8 not-prose rounded-r-lg" data-id="element-110">
                <p className="font-serif text-base md:text-lg text-emerald-900 italic font-medium m-0" data-id="element-111">
                  "The alarm isn't waking Jake up. It's attacking him."
                </p>
              </div>

              <p data-id="element-112">
                The doctor explained something called the Auditory Overload
                Cascade.
              </p>

              <p data-id="element-113">Here's what happens:</p>

              <p data-id="element-114">
                When a loud alarm goes off, most kids wake up slowly and calmly.
              </p>

              <p data-id="element-115">
                But for kids with ADHD or autism, their brains work differently.
              </p>

              <ul className="list-none pl-0 space-y-4 my-8 not-prose" data-id="element-116">
                <li className="flex items-start gap-3" data-id="element-117">
                  <div className="bg-emerald-100 text-emerald-700 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1 font-bold text-xs" data-id="element-118">
                    1
                  </div>
                  <span className="text-slate-700" data-id="element-119">
                    <strong className="text-slate-900" data-id="element-120">
                      The alarm sound hits their ears while they're still
                      asleep.
                    </strong>{' '}
                    Their brain can't filter it properly.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-121">
                  <div className="bg-emerald-100 text-emerald-700 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1 font-bold text-xs" data-id="element-122">
                    2
                  </div>
                  <span className="text-slate-700" data-id="element-123">
                    <strong className="text-slate-900" data-id="element-124">
                      Instead of a gentle wake-up, the sound triggers instant
                      panic mode.
                    </strong>{' '}
                    Their nervous system floods with stress chemicals.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-125">
                  <div className="bg-emerald-100 text-emerald-700 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1 font-bold text-xs" data-id="element-126">
                    3
                  </div>
                  <span className="text-slate-700" data-id="element-127">
                    <strong className="text-slate-900" data-id="element-128">
                      Their heart starts racing. Their body goes into
                      fight-or-flight.
                    </strong>{' '}
                    All of this happens BEFORE they're even awake.
                  </span>
                </li>
              </ul>

              <p data-id="element-129">
                By the time their eyes open, they're already in full meltdown
                mode.
              </p>

              <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 md:p-6 my-8 not-prose rounded-r-lg" data-id="element-130">
                <p className="font-serif text-base md:text-lg text-emerald-900 italic font-medium m-0" data-id="element-131">
                  "It's like being startled awake by a fire alarm every single
                  morning. Except for your son, it happens with his regular
                  alarm."
                </p>
              </div>

              <p data-id="element-132">
                Sarah finally understood. Jake wasn't being difficult. His brain
                was literally being attacked the moment he woke up.
              </p>

              {/* SECTION 4 - Why Nothing Else Works */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-133">
                Why Nothing Else Works
              </h2>

              <p data-id="element-134">
                The doctor explained why all of Sarah's attempts had failed:
              </p>

              <ul className="not-prose space-y-4 my-6" data-id="element-135">
                <li className="flex gap-3" data-id="element-136">
                  <X className="text-red-500 flex-shrink-0 mt-1" data-id="element-137" />
                  <span className="text-slate-700" data-id="element-138">
                    <strong data-id="element-139">Louder alarms?</strong> That just makes the overload
                    worse.
                  </span>
                </li>
                <li className="flex gap-3" data-id="element-140">
                  <X className="text-red-500 flex-shrink-0 mt-1" data-id="element-141" />
                  <span className="text-slate-700" data-id="element-142">
                    <strong data-id="element-143">Multiple alarms?</strong> That's multiple panic
                    triggers in a row.
                  </span>
                </li>
                <li className="flex gap-3" data-id="element-144">
                  <X className="text-red-500 flex-shrink-0 mt-1" data-id="element-145" />
                  <span className="text-slate-700" data-id="element-146">
                    <strong data-id="element-147">Wake-up lights?</strong> They don't stop the sound
                    problem.
                  </span>
                </li>
                <li className="flex gap-3" data-id="element-148">
                  <X className="text-red-500 flex-shrink-0 mt-1" data-id="element-149" />
                  <span className="text-slate-700" data-id="element-150">
                    <strong data-id="element-151">Waking him up yourself?</strong> You can't do that
                    forever. Plus, it doesn't teach him to wake up on his own.
                  </span>
                </li>
              </ul>

              <p data-id="element-152">
                "All of these solutions ignore the real problem," the doctor
                said. "The problem is the sound itself."
              </p>

              <p data-id="element-153">And the consequences aren't just about hard mornings.</p>

              <p data-id="element-154">
                When your child starts every day in panic mode, it affects
                everything. Their anxiety gets worse. Their confidence drops.
                They start to believe they can't do anything right.
              </p>

              <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 md:p-6 my-8 not-prose rounded-r-lg" data-id="element-155">
                <p className="font-serif text-base md:text-lg text-emerald-900 italic font-medium m-0" data-id="element-156">
                  "Every morning your child wakes up overwhelmed, their brain
                  learns to expect stress. Over time, this becomes their normal.
                  And that's really hard to undo."
                </p>
              </div>

              <p data-id="element-157">
                Sarah felt sick. She'd been doing this to Jake for years without
                knowing it.
              </p>

              <p data-id="element-158">But then the doctor said something that gave her hope:</p>

              <p className="font-bold text-slate-900" data-id="element-159">
                "There's a simple solution. And it works with Jake's brain, not
                against it."
              </p>

              {/* SECTION 5 - The Solution That Changes Everything */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-160">
                The Solution That Changes Everything
              </h2>

              <p data-id="element-161">
                The doctor pulled out a small bracelet and put it on Sarah's
                wrist.
              </p>

              <p data-id="element-162">"Feel this," he said.</p>

              <p data-id="element-163">
                A gentle vibration pulsed against her skin. It was soft. Calm.
                Nothing like an alarm.
              </p>

              <p data-id="element-164">
                "This is called Tactile Priority Processing," he explained.
                "Instead of sound, it uses touch to wake up the body."
              </p>

              <p data-id="element-165">
                The bracelet was called the <strong data-id="element-166">Nymera CalmRise</strong>.
                And it works completely differently than a regular alarm.
              </p>

              <img src="https://cdn.magicpatterns.com/uploads/5KJqwU1uHMV6LLoFZbugHf/ChatGPT_Image_14_Φεβ_2026,_03_14_39_μ.μ..png" alt="Nymera CalmRise vibration alarm bracelet on a child's wrist" className="w-full rounded-xl my-8 shadow-lg" data-id="element-167" />

              <p data-id="element-168">Here's how:</p>

              <div className="bg-emerald-50 p-4 md:p-6 rounded-xl border border-emerald-100 my-8 not-prose" data-id="element-169">
                <ul className="space-y-3" data-id="element-170">
                  <li className="flex items-start gap-3" data-id="element-171">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-172">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-173" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-174">
                      Instead of blasting sound into your child's ears, CalmRise
                      sends a gentle vibration to their wrist
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-175">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-176">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-177" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-178">
                      Their nervous system processes this touch signal faster
                      than sound — but doesn't see touch as a threat
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-179">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-180">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-181" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-182">
                      The vibration wakes up your child's body first, then their
                      brain — without any stress response
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-183">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-184">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-185" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-186">
                      No panic. No overwhelm. Just a calm, gradual wake-up
                    </span>
                  </li>
                </ul>
              </div>

              <p data-id="element-187">It made perfect sense to Sarah. But she was skeptical.</p>

              <p data-id="element-188">"Will it actually wake him up?" she asked.</p>

              <p data-id="element-189">
                "Try it for a week," the doctor said. "If it doesn't work, bring
                it back."
              </p>

              {/* SECTION 6 - The First Morning */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-190">
                The First Morning
              </h2>

              <p data-id="element-191">
                Sarah put the Nymera CalmRise on Jake's wrist that night. She
                set it for 6:45 AM. And she barely slept, worried about what
                would happen.
              </p>

              <p data-id="element-192">
                The next morning, she stood outside his door, bracing herself
                for the usual screaming.
              </p>

              <p data-id="element-193">6:45 came.</p>

              <p className="font-bold text-slate-900 text-xl" data-id="element-194">Silence.</p>

              <p data-id="element-195">
                Then she heard Jake's feet hit the floor. He walked to his
                dresser. Started getting dressed.
              </p>

              <p data-id="element-196">Sarah stood there in shock.</p>

              <p data-id="element-197">
                When Jake came out of his room, he looked... calm. Normal. Like
                any other kid on any other morning.
              </p>

              <p data-id="element-198">"Hey Mom," he said.</p>

              <p className="font-bold text-slate-900" data-id="element-199">Sarah started crying.</p>

              <img src="https://cdn.magicpatterns.com/uploads/2FR456F2LoEdZ215YsGiz2/ChatGPT_Image_14_Φεβ_2026,_03_14_37_μ.μ..png" alt="Calm child getting ready for school independently" className="w-full rounded-xl my-8 shadow-lg" data-id="element-200" />

              <p data-id="element-201">
                "Within three days, our mornings were completely different," she
                says now. "Jake woke up without screaming. He got out of bed on
                his own. He wasn't angry or upset."
              </p>

              <p data-id="element-202">Within two weeks, the morning battles were completely gone.</p>

              <p data-id="element-203">
                No more tardy slips. No more fights. No more feeling like a
                failure before 8 AM.
              </p>

              <p className="font-bold text-slate-900" data-id="element-204">
                "I got my son back," Sarah says. "And I got my mornings back."
              </p>

              <p data-id="element-205">But the changes went deeper than that.</p>

              <p data-id="element-206">
                Jake's anxiety dropped. His confidence improved. His teachers
                noticed he was calmer and more focused at school.
              </p>

              <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 md:p-6 my-8 not-prose rounded-r-lg" data-id="element-207">
                <p className="font-serif text-base md:text-lg text-emerald-900 italic font-medium m-0" data-id="element-208">
                  "It wasn't just about waking up. It was about how he started
                  every single day."
                </p>
              </div>

              {/* SECTION 7 - It's Not Just Sarah */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-209">
                It's Not Just Sarah
              </h2>

              <p data-id="element-210">
                Since then, over 28,000 families have discovered the Nymera
                CalmRise.
              </p>

              <p data-id="element-211">And the stories are almost identical:</p>

              <div className="not-prose grid gap-6" data-id="element-212">
                <TestimonialCard headline="Life-changing for our whole family" quote="My autistic daughter used to scream when any alarm went off. Now she wakes up calm every morning. It's been life-changing for our whole family." author="Michelle R." location="Austin, TX" avatarUrl="https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-213" />
                <TestimonialCard headline="Both boys waking up on their own" quote="I have twin boys with ADHD. Mornings were a nightmare. Within one week of using CalmRise, both boys were waking up on their own without meltdowns. I can't believe we didn't know about this sooner." author="David K." location="Chicago, IL" avatarUrl="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-214" />
                <TestimonialCard headline="He actually wakes up smiling now" quote="My son has severe sensory issues. Regular alarms sent him into full panic attacks. The CalmRise vibration is so gentle that he actually wakes up smiling now. I never thought I'd see that." author="Jennifer L." location="Seattle, WA" avatarUrl="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-215" />
                <TestimonialCard headline="Solved our morning problems in less than a week" quote="We tried everything. Reward charts, threats, bribery, nothing worked. Nymera CalmRise solved our morning problems in less than a week." author="Marcus T." location="Atlanta, GA" avatarUrl="https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-216" />
              </div>

              <p className="mt-8" data-id="element-217">The results are consistent:</p>

              <div className="bg-emerald-50 p-4 md:p-6 rounded-xl border border-emerald-100 my-8 not-prose" data-id="element-218">
                <ul className="space-y-3" data-id="element-219">
                  <li className="flex items-start gap-3" data-id="element-220">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-221">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-222" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-223">
                      Calmer wake-ups
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-224">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-225">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-226" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-227">
                      Independent mornings
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-228">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-229">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-230" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-231">
                      Better school attendance
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-232">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-233">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-234" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-235">
                      Less anxiety
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-236">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-237">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-238" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-239">
                      Improved parent-child relationships
                    </span>
                  </li>
                </ul>
              </div>

              <p data-id="element-240">And all because they stopped the Auditory Overload Cascade.</p>

              {/* SECTION 8 - Common Questions */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-241">
                Common Questions Parents Have
              </h2>

              <div className="not-prose space-y-2 mb-12" data-id="element-242">
                <FAQItem question="Will my child actually wake up to just a vibration?" answer="Yes. The CalmRise vibration is stronger than a phone vibrate. It starts gentle, then gradually gets stronger if needed. Kids feel it immediately, even in deep sleep." data-id="element-243" />
                <FAQItem question="What if they take it off at night?" answer="The CalmRise bracelet is comfortable and adjustable. Most kids forget they're wearing it. Plus, once they experience calm mornings, they actually want to wear it." data-id="element-244" />
                <FAQItem question="Is it safe?" answer="Completely. It's made specifically for kids. No radiation. No chemicals. Hypoallergenic materials. Thousands of pediatricians now recommend it." data-id="element-245" />
                <FAQItem question="Can they use it for other reminders during the day?" answer="Yes! Many kids use CalmRise at school for task transitions, medication reminders, or focusing breaks. Teachers love it because it's silent and doesn't disrupt the class." data-id="element-246" />
              </div>

              {/* SECTION 9 - Why This Matters Right Now */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-247">
                Why This Matters Right Now
              </h2>

              <p data-id="element-248">Word about Nymera CalmRise is spreading fast.</p>

              <p data-id="element-249">
                Pediatricians are recommending it. ADHD parent groups are
                sharing it. Schools are even allowing kids to use it in class.
              </p>

              <p className="font-bold text-slate-900" data-id="element-250">
                But here's what really matters:
              </p>

              <p data-id="element-251">
                Every morning your child wakes up in panic mode, it gets harder
                to change the pattern.
              </p>

              <p data-id="element-252">
                Their brain gets used to starting the day with stress. Their
                anxiety builds. Their confidence drops.
              </p>

              <p data-id="element-253">
                The sooner you stop the Auditory Overload Cascade, the better.
              </p>

              {/* SECTION 10 - What You Get */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-254">
                What You Get
              </h2>

              <p data-id="element-255">The Nymera CalmRise includes:</p>

              <div className="bg-emerald-50 p-4 md:p-6 rounded-xl border border-emerald-100 my-8 not-prose" data-id="element-256">
                <ul className="space-y-3" data-id="element-257">
                  <li className="flex items-start gap-3" data-id="element-258">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-259">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-260" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-261">
                      Adjustable, comfortable wristband (fits ages 5+)
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-262">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-263">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-264" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-265">
                      Multiple alarm settings
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-266">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-267">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-268" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-269">
                      Vibration strength control
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-270">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-271">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-272" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-273">
                      Waterproof design
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-274">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-275">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-276" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-277">
                      Rechargeable battery (lasts 7+ days)
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-278">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-279">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-280" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-281">
                      Silent operation (won't wake siblings)
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-282">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-283">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-284" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-285">
                      Free smartphone app for easy setup
                    </span>
                  </li>
                </ul>
              </div>

              {/* SECTION 11 - The Investment */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-286">
                The Investment
              </h2>

              <p data-id="element-287">
                When Sarah first learned about Nymera CalmRise, she was ready to
                pay anything.
              </p>

              <p data-id="element-288">
                "I would have paid hundreds to fix our mornings," she says. "The
                therapy sessions alone were costing us way more than this."
              </p>

              <p data-id="element-289">But Nymera CalmRise isn't expensive.</p>

              <p data-id="element-290">
                And right now, parents reading this can save 30% off the regular
                price.
              </p>

              <p data-id="element-291">That's real savings just for taking action today.</p>

              <div className="bg-slate-100 p-4 md:p-6 rounded-lg my-8 not-prose" data-id="element-292">
                <h4 className="font-bold text-slate-900 mb-2" data-id="element-293">
                  60-Day Money-Back Guarantee
                </h4>
                <p className="text-slate-700 mb-4" data-id="element-294">
                  If you don't see calmer mornings in the first week, send it
                  back. You risk nothing.
                </p>
                <div className="flex items-center gap-2 text-emerald-700 font-bold text-sm" data-id="element-295">
                  <ShieldCheck size={20} data-id="element-296" />
                  <span data-id="element-297">Risk-Free Trial</span>
                </div>
              </div>

              {/* SECTION 12 - Two Choices */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-298">
                Two Choices
              </h2>

              <p data-id="element-299">You have two choices right now.</p>

              <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 my-8" data-id="element-300">
                {/* Choice One */}
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 md:p-6" data-id="element-301">
                  <h4 className="font-sans font-bold text-lg text-red-800 mb-4" data-id="element-302">
                    Choice One:
                  </h4>
                  <p className="text-slate-700 text-sm leading-relaxed" data-id="element-303">
                    Tomorrow morning, same alarm goes off. Same meltdown
                    happens. Same battle before school. Your child starts
                    another day in panic mode. You feel like a failure again.
                    The tardy slips keep coming. And every day this continues,
                    the pattern gets harder to break.
                  </p>
                </div>

                {/* Choice Two */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 md:p-6" data-id="element-304">
                  <h4 className="font-sans font-bold text-lg text-emerald-800 mb-4" data-id="element-305">
                    Choice Two:
                  </h4>
                  <p className="text-slate-700 text-sm leading-relaxed" data-id="element-306">
                    Tomorrow morning, gentle vibration wakes your child up. They
                    open their eyes calm and aware. They get out of bed on their
                    own. No screaming. No fighting. No tears. You both actually
                    smile at breakfast.
                  </p>
                </div>
              </div>

              <p data-id="element-307">
                Imagine that morning. Now imagine a week of those mornings. A
                month. A year.
              </p>

              <p data-id="element-308">
                No more war zone. No more guilt. No more dreading the alarm.
              </p>

              <p data-id="element-309">
                Just calm, peaceful mornings where your child wakes up feeling
                capable and you feel like the parent you want to be.
              </p>

              {/* SECTION 13 - Final CTA */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-310">
                What Kind Of Morning Do You Want Tomorrow?
              </h2>

              <p data-id="element-311">Your child deserves to wake up calm.</p>

              <p data-id="element-312">You deserve peaceful mornings.</p>

              <p data-id="element-313">Nymera CalmRise makes both possible.</p>

              <p className="font-bold text-slate-900" data-id="element-314">
                Join the 28,000+ families who've already ended the morning
                battles.
              </p>

              {/* Main CTA Offer Box */}
              <div id="offer-section" className="bg-white border-2 border-emerald-500 rounded-2xl p-6 md:p-10 shadow-2xl my-12 not-prose relative overflow-hidden" data-id="element-315">
                <div className="absolute top-0 right-0 bg-emerald-500 text-white text-xs font-bold px-4 py-1 rounded-bl-lg" data-id="element-316">
                  LIMITED TIME OFFER
                </div>

                <h2 className="font-sans font-bold text-3xl text-slate-900 mb-2 text-center" data-id="element-317">
                  Special Offer — Save 30% Today
                </h2>
                <p className="text-center text-slate-600 mb-8" data-id="element-318">
                  Get your Nymera CalmRise at the lowest price
                </p>

                <div className="flex flex-col md:flex-row gap-8 items-center mb-8" data-id="element-319">
                  <div className="flex-1" data-id="element-320">
                    <ul className="space-y-3" data-id="element-321">
                      <li className="flex items-center gap-2 text-slate-700 text-sm md:text-base" data-id="element-322">
                        <Check size={18} className="text-emerald-500 flex-shrink-0" data-id="element-323" />{' '}
                        Adjustable wristband (ages 5+)
                      </li>
                      <li className="flex items-center gap-2 text-slate-700 text-sm md:text-base" data-id="element-324">
                        <Check size={18} className="text-emerald-500 flex-shrink-0" data-id="element-325" />{' '}
                        Waterproof & rechargeable
                      </li>
                      <li className="flex items-center gap-2 text-slate-700 text-sm md:text-base" data-id="element-326">
                        <Check size={18} className="text-emerald-500 flex-shrink-0" data-id="element-327" />{' '}
                        60-Day Money-Back Guarantee
                      </li>
                      <li className="flex items-center gap-2 text-slate-700 text-sm md:text-base" data-id="element-328">
                        <Check size={18} className="text-emerald-500 flex-shrink-0" data-id="element-329" />{' '}
                        Pediatrician Recommended
                      </li>
                      <li className="flex items-center gap-2 text-slate-700 text-sm md:text-base" data-id="element-330">
                        <Check size={18} className="text-emerald-500 flex-shrink-0" data-id="element-331" />{' '}
                        Free Shipping
                      </li>
                    </ul>
                  </div>

                  <div className="flex-1 w-full" data-id="element-332">
                    <Button fullWidth size="xl" onClick={scrollToOffer} className="mb-3 shadow-emerald-200 shadow-xl" data-id="element-333">
                      GET 30% OFF NYMERA CALMRISE NOW
                    </Button>
                    <div className="flex items-center justify-center gap-4 text-xs text-slate-500" data-id="element-334">
                      <span className="flex items-center gap-1" data-id="element-335">
                        <Truck size={12} data-id="element-336" /> Free Shipping
                      </span>
                      <span className="flex items-center gap-1" data-id="element-337">
                        <Clock size={12} data-id="element-338" /> Limited Time
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-center text-sm text-slate-600 font-medium" data-id="element-339">
                  60-Day Money-Back Guarantee – Risk-Free
                </p>
                <p className="text-center text-sm text-slate-500 italic mt-2" data-id="element-340">
                  Your child's next calm morning is just one click away.
                </p>
              </div>
            </article>
          </main>

          {/* Sticky Sidebar CTA - Desktop Only */}
          <aside className="hidden lg:block" data-id="element-341">
            <StickyCTA onCtaClick={scrollToOffer} data-id="element-342" />
          </aside>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-200 py-12 px-4" data-id="element-343">
        <div className="max-w-3xl mx-auto text-center" data-id="element-344">
          <div className="mb-8" data-id="element-345">
            <div className="font-bold text-xl tracking-tight text-slate-900 mb-4" data-id="element-346">
              Parenting<span className="text-emerald-600" data-id="element-347">Today</span>
            </div>
          </div>

          <div className="text-[10px] text-slate-400 space-y-4 leading-relaxed border-t border-slate-200 pt-8" data-id="element-348">
            <p data-id="element-349">
              This is an advertisement and not an actual news article, blog, or
              consumer protection update. This website is not intended to
              provide medical advice or to take the place of medical advice from
              your personal physician. Nymera CalmRise is not a medical device
              and is not intended to diagnose, treat, cure, or prevent any
              medical condition. Consult your child's doctor before making
              changes to their routine. Results may vary. Testimonials represent
              individual experiences and are not guaranteed outcomes.
            </p>
            <p data-id="element-350">
              Trademarks utilized on our website belong to their respective
              owners. Through research and experienced editors we provide
              feedback about products. We are independently owned. This website
              is supported by referral fees from featured products.
            </p>
            <div className="flex justify-center gap-4 pt-4" data-id="element-351">
              <a href="#" className="hover:text-slate-600" data-id="element-352">
                About Us
              </a>
              <a href="#" className="hover:text-slate-600" data-id="element-353">
                Terms of Use
              </a>
              <a href="#" className="hover:text-slate-600" data-id="element-354">
                Privacy Policy
              </a>
            </div>
            <p className="pt-4" data-id="element-355">©2025 Parenting Today. All Rights Reserved.</p>
          </div>
        </div>
      </footer>

      {/* Mobile Sticky CTA */}
      <div className={`fixed bottom-0 left-0 right-0 bg-white border-t-2 border-emerald-500 shadow-[0_-8px_16px_-4px_rgba(0,0,0,0.15)] transition-transform duration-300 z-50 lg:hidden ${showStickyCTA ? 'translate-y-0' : 'translate-y-full'}`} data-id="element-356">
        <div className="p-4 pb-6" data-id="element-357">
          <div className="flex items-center justify-between mb-3" data-id="element-358">
            <div data-id="element-359">
              <p className="font-bold text-sm text-slate-900" data-id="element-360">
                End Morning Meltdowns
              </p>
              <div className="flex items-center gap-1" data-id="element-361">
                {[...Array(5)].map((_, i) => <Star key={i} size={12} fill="#FCD34D" className="text-yellow-400" data-id="element-362" />)}
                <span className="text-xs text-slate-600 ml-1" data-id="element-363">
                  4.9 (28,000+)
                </span>
              </div>
            </div>
            <div className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-364">
              30% OFF
            </div>
          </div>
          <Button fullWidth onClick={scrollToOffer} size="lg" data-id="element-365">
            GET YOUR CALMRISE →
          </Button>
          <p className="text-[10px] text-center text-slate-500 mt-2" data-id="element-366">
            60-Day Guarantee • Free Shipping
          </p>
        </div>
      </div>
    </div>;
}